// Client-side mirror of `shared::physics::sweep_aabb`. Same algorithm,
// same constants, same SKIN offset — only the math is f64 instead of
// Q16.16 fixed-point. Tiny per-tick drift between the two layers
// (different rounding behavior) gets absorbed into `renderOffset` by
// the snapshot reconcile path; no need for bit-exact parity.
//
// Why mirror at all: without client-side collision the prediction
// engine cheerfully walks the predicted self position straight through
// any wall. The server's swept-AABB then snaps the player back on the
// next snapshot, producing a noticeable rubber-band when you crash
// into cover. With this mirror, the predicted position respects walls
// the same way the server does and the reconcile delta stays sub-cell
// in normal play.
//
// Direct port of `shared/src/physics/sweep.rs`:
//   - `sweepAABB`         ← `sweep_aabb`
//   - `earliestCellContact` ← `earliest_cell_contact`
//   - `sweepVsCell`       ← `sweep_vs_cell`
//
// The static push-out half of the server's sweep is intentionally
// omitted. Reconcile already snaps us to the server's swept position
// (which is post-push-out), so the client never needs to re-extract
// itself from an overlap.

/** Maximum slide iterations per call. Counter-strafing into a corner
 *  of two perpendicular walls converges in 2; the cap of 4 covers all
 *  realistic geometries without unbounded cost. Matches the server. */
const MAX_SLIDE_ITERS = 4;

/** Tiny offset along the contact normal so the AABB rests just shy of
 *  the wall — prevents the next sweep from re-contacting the same
 *  surface at t = 0. Matches `SKIN = 64 / 65536` on the server. */
const SKIN = 64 / 65536;

/** True if the given cell coordinate currently contains a wall. The
 *  client passes a closure that wraps a `Set<packCellKey>` lookup. */
export type WallLookup = (cellX: number, cellY: number) => boolean;

/** Player circle radius for `sweepCircle`. Must match the server's
 *  `PLAYER_HALF.x` so client prediction and server authoritative agree
 *  on what motion gets clipped. */
const CIRCLE_RADIUS = 0.7;

export interface SweepResult {
  finalX: number;
  finalY: number;
  /** Surface normal of the last contact during this move. Both zero
   *  if the move completed without touching any wall. */
  hitNormalX: number;
  hitNormalY: number;
  iters: number;
}

/** Sweep a player AABB (square, `half × half`) through the wall grid.
 *
 *  - `(startX, startY)`: AABB center at the start of the tick.
 *  - `half`:             half-extent on both axes (server's PLAYER_HALF).
 *  - `(deltaX, deltaY)`: desired motion this tick (= velocity).
 *  - `isWall`:           grid lookup callback.
 *
 *  Returns the final position + last-hit normal. Velocity is NOT
 *  modified by sweep — the caller keeps stepping it the same way
 *  whether or not a wall was hit (matches `apply_movement` which only
 *  writes `pos.0`, not `vel.0`, from the sweep result).
 */
export function sweepAABB(
  startX: number,
  startY: number,
  half: number,
  deltaX: number,
  deltaY: number,
  isWall: WallLookup,
): SweepResult {
  let posX = startX;
  let posY = startY;
  let remX = deltaX;
  let remY = deltaY;
  let lastNX = 0;
  let lastNY = 0;
  let iters = 0;

  while (iters < MAX_SLIDE_ITERS) {
    if (remX === 0 && remY === 0) break;
    const hit = earliestCellContact(posX, posY, half, remX, remY, isWall);
    if (hit === null) {
      // No contact — apply full remaining motion and we're done.
      posX += remX;
      posY += remY;
      iters++;
      break;
    }
    // Step up to contact, plus a skin offset along the normal so the
    // next sweep doesn't immediately re-contact the same surface.
    posX = posX + remX * hit.toi + hit.normalX * SKIN;
    posY = posY + remY * hit.toi + hit.normalY * SKIN;

    // Slide: leftover motion minus its component along the normal.
    const leftoverX = remX * (1 - hit.toi);
    const leftoverY = remY * (1 - hit.toi);
    const dot = leftoverX * hit.normalX + leftoverY * hit.normalY;
    remX = leftoverX - hit.normalX * dot;
    remY = leftoverY - hit.normalY * dot;

    lastNX = hit.normalX;
    lastNY = hit.normalY;
    iters++;
  }

  return { finalX: posX, finalY: posY, hitNormalX: lastNX, hitNormalY: lastNY, iters };
}

interface Contact {
  toi: number;
  normalX: number;
  normalY: number;
}

/** Broadphase: enumerate every cell whose AABB the swept body could
 *  conceivably enter (bbox of start + end positions inflated by `half`),
 *  pick the earliest hit. */
function earliestCellContact(
  posX: number,
  posY: number,
  half: number,
  deltaX: number,
  deltaY: number,
  isWall: WallLookup,
): Contact | null {
  const p2x = posX + deltaX;
  const p2y = posY + deltaY;
  const loX = Math.min(posX, p2x) - half;
  const loY = Math.min(posY, p2y) - half;
  const hiX = Math.max(posX, p2x) + half;
  const hiY = Math.max(posY, p2y) + half;
  const cxLo = Math.floor(loX);
  const cyLo = Math.floor(loY);
  const cxHi = Math.floor(hiX);
  const cyHi = Math.floor(hiY);

  let bestToi = Infinity;
  let bestNX = 0;
  let bestNY = 0;
  for (let cy = cyLo; cy <= cyHi; cy++) {
    for (let cx = cxLo; cx <= cxHi; cx++) {
      if (!isWall(cx, cy)) continue;
      const hit = sweepVsCell(posX, posY, half, deltaX, deltaY, cx, cy);
      if (hit !== null && hit.toi < bestToi) {
        bestToi = hit.toi;
        bestNX = hit.normalX;
        bestNY = hit.normalY;
      }
    }
  }
  if (bestToi === Infinity) return null;
  return { toi: bestToi, normalX: bestNX, normalY: bestNY };
}

/** Slab-method sweep of a moving AABB against a single static unit
 *  cell at integer coords `(cx, cy)`. The static cell is inflated by
 *  `half` on each side, which lets us treat the moving AABB as a point
 *  ray against that inflated rectangle. */
function sweepVsCell(
  posX: number,
  posY: number,
  half: number,
  deltaX: number,
  deltaY: number,
  cx: number,
  cy: number,
): Contact | null {
  const iminX = cx - half;
  const imaxX = cx + 1 + half;
  const iminY = cy - half;
  const imaxY = cy + 1 + half;

  // Sentinels well outside [0, 1]; we never do arithmetic on them.
  let tEnter = -1000;
  let tExit = 1000;
  let normalX = 0;
  let normalY = 0;

  if (deltaX === 0) {
    if (posX <= iminX || posX >= imaxX) return null;
  } else {
    const t1 = (iminX - posX) / deltaX;
    const t2 = (imaxX - posX) / deltaX;
    let tn: number;
    let tf: number;
    let nx: number;
    if (t1 < t2) {
      tn = t1;
      tf = t2;
      nx = -1;
    } else {
      tn = t2;
      tf = t1;
      nx = 1;
    }
    if (tn > tEnter) {
      tEnter = tn;
      normalX = nx;
      normalY = 0;
    }
    if (tf < tExit) tExit = tf;
  }

  if (deltaY === 0) {
    if (posY <= iminY || posY >= imaxY) return null;
  } else {
    const t1 = (iminY - posY) / deltaY;
    const t2 = (imaxY - posY) / deltaY;
    let tn: number;
    let tf: number;
    let ny: number;
    if (t1 < t2) {
      tn = t1;
      tf = t2;
      ny = -1;
    } else {
      tn = t2;
      tf = t1;
      ny = 1;
    }
    if (tn > tEnter) {
      tEnter = tn;
      normalX = 0;
      normalY = ny;
    }
    if (tf < tExit) tExit = tf;
  }

  if (tEnter > tExit || tEnter > 1 || tExit < 0) return null;
  const toi = tEnter < 0 ? 0 : tEnter;
  return { toi, normalX, normalY };
}

// ---------------------------------------------------------------------------
// Circle sweep — mirrors `shared::physics::sweep_circle`.
//
// `sweepAABB` above treats the player as a square. With a circular
// visual that's wrong at corners: the AABB's diagonal corners stick
// out to radius·√2 from center; the visible circle only reaches
// radius. Walking diagonally past a wall corner stops dead when the
// AABB corners catch the cell corner, even though the visible circle
// would clear it with room to spare.
//
// `sweepCircle` runs the same slide-on-contact loop but the inner
// cell test is circle-vs-AABB instead of AABB-vs-AABB. Face hits use
// the slab method on the cell inflated by R; corner hits do a
// ray-vs-circle test against the cell corner with radius R. Same
// constants (`MAX_SLIDE_ITERS`, `SKIN`) as the AABB version so the
// behavior is byte-similar where the geometries agree.
// ---------------------------------------------------------------------------

/** Move a circular player through the wall grid: apply `delta` first,
 *  then iteratively push the circle out of any cells it overlaps.
 *  Byte-identical algorithm to `shared::sweep_circle` so client
 *  prediction and server authoritative state agree on collision
 *  results (modulo Q16.16 ↔ f64 rounding, which `renderOffset`
 *  absorbs).
 *
 *  At a flat face the push direction is cardinal; at a CORNER it's
 *  diagonal (the closest-point on the cell AABB IS the corner, so
 *  the push runs through the corner toward the player center). That
 *  diagonal push is what makes the player roll around wall corners
 *  instead of stopping dead — the "circle rounds the corner" feel
 *  the previous swept-AABB / Minkowski sweep couldn't produce
 *  smoothly.
 *
 *  Safety: max per-tick motion (~0.22 cells) is well under the
 *  tunneling threshold (`2 * radius ≈ 1.4`), so the move-first /
 *  resolve-second order is safe — the circle can never skip past a
 *  wall in a single tick. */
export function sweepCircle(
  startX: number,
  startY: number,
  radius: number,
  deltaX: number,
  deltaY: number,
  isWall: WallLookup,
): SweepResult {
  let posX = startX + deltaX;
  let posY = startY + deltaY;
  const rSq = radius * radius;
  let lastNX = 0;
  let lastNY = 0;
  let iters = 0;

  for (let pass = 0; pass < MAX_SLIDE_ITERS; pass++) {
    let pushed = false;
    const minCx = Math.floor(posX - radius);
    const maxCx = Math.floor(posX + radius);
    const minCy = Math.floor(posY - radius);
    const maxCy = Math.floor(posY + radius);

    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        if (!isWall(cx, cy)) continue;
        // Closest point on the cell's AABB to the player center.
        const nx = posX < cx ? cx : posX > cx + 1 ? cx + 1 : posX;
        const ny = posY < cy ? cy : posY > cy + 1 ? cy + 1 : posY;
        const dx = posX - nx;
        const dy = posY - ny;
        const dSq = dx * dx + dy * dy;
        if (dSq >= rSq) continue;
        if (dSq < 1e-6) {
          // Center coincident with the AABB boundary — fall back to
          // pushing from cell CENTER. Mirrors the reference HTML's
          // `d < 0.0001` branch + the Rust `EPS_SQ` check.
          const ccx = cx + 0.5;
          const ccy = cy + 0.5;
          const pdx = posX - ccx;
          const pdy = posY - ccy;
          const pdSq = pdx * pdx + pdy * pdy;
          const push = radius + 0.5;
          if (pdSq < 1e-6) {
            posX = ccx + push;
            lastNX = 1;
            lastNY = 0;
          } else {
            const pd = Math.sqrt(pdSq);
            posX = ccx + (pdx / pd) * push;
            posY = ccy + (pdy / pd) * push;
            lastNX = pdx / pd;
            lastNY = pdy / pd;
          }
        } else {
          const d = Math.sqrt(dSq);
          const push = radius - d + SKIN;
          posX = posX + (dx / d) * push;
          posY = posY + (dy / d) * push;
          lastNX = dx / d;
          lastNY = dy / d;
        }
        pushed = true;
      }
    }
    if (!pushed) break;
    iters++;
  }

  return { finalX: posX, finalY: posY, hitNormalX: lastNX, hitNormalY: lastNY, iters };
}

/** Convenience wrapper that uses the default `CIRCLE_RADIUS`. Keeps
 *  call sites short. */
export function sweepPlayerCircle(
  startX: number,
  startY: number,
  deltaX: number,
  deltaY: number,
  isWall: WallLookup,
): SweepResult {
  return sweepCircle(startX, startY, CIRCLE_RADIUS, deltaX, deltaY, isWall);
}

